import { useCallback, useMemo } from 'react';
import { LogisticProviderListFilters } from '@/api/apis/facilities';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { UseFiltersType } from '@/hooks/useFilters';

export const useLogisticProviderFilters = ({
  filters,
  updateFilters,
}: Pick<UseFiltersType<LogisticProviderListFilters>, 'filters' | 'updateFilters'>) => {
  const { countriesSelectList, countriesByIso2, subdivisions, serviceRegions } = useCountriesData();

  const serviceRegionItems = useMemo(
    () => Object.values(serviceRegions).map(({ id, name }) => ({ value: id, name })),
    [serviceRegions],
  );

  const countriesDropdownItems = useMemo(() => {
    if (!filters.serviceRegionIds) return countriesSelectList;
    return countriesSelectList.filter(
      ({ value }) => countriesByIso2[value]?.serviceRegionId === filters.serviceRegionIds,
    );
  }, [filters.serviceRegionIds, countriesSelectList, countriesByIso2]);

  const statesSelectList = useMemo(
    () =>
      filters.countries.flatMap((alpha2) =>
        (subdivisions[alpha2] ?? []).map((s) => ({ value: s.code, name: `${alpha2} – ${s.name}` })),
      ),
    [filters.countries, subdivisions],
  );

  const handleRegionChange = useCallback(
    (values: string[]) => {
      const regionId = values[0] ?? '';
      updateFilters(regionId, 'serviceRegionIds');
      updateFilters([], 'countries');
      updateFilters([], 'states');
    },
    [updateFilters],
  );

  const handleCountriesChange = useCallback(
    (values: string[]) => {
      updateFilters(values, 'countries');
      updateFilters([], 'states');
    },
    [updateFilters],
  );

  return {
    serviceRegionItems,
    countriesDropdownItems,
    countriesSelectedKeys: filters.countries,
    statesSelectList,
    handleRegionChange,
    handleCountriesChange,
  };
};
